import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type {
  ApprovalFlow,
  ApprovalFlowStep,
  User,
} from '@ulysses-inc/harami_api_client'
import type { FormikErrors, FormikTouched } from 'formik'
import type React from 'react'
import { useCallback } from 'react'

interface OwnProps {
  steps?: ApprovalFlowStep[]
  users: User[]
  touched: FormikTouched<ApprovalFlow>
  errors: FormikErrors<ApprovalFlow>
  setFieldValue: (
    field: string,
    value: ApprovalFlowStep[] | undefined,
    shouldValidate?: boolean,
  ) => void
}

const EditApprovalFlowSteps: React.FC<OwnProps> = ({
  users,
  steps,
  setFieldValue,
}) => {
  const onChangeApprover = useCallback(
    (stepIndex: number, approverIndex: number) => {
      return (value: unknown) => {
        const newValue = steps?.map((step, i) => {
          // 対象のステップ以外はそのまま返す
          if (i !== stepIndex) {
            return step
          }

          // 対象のステップの場合、新しいstepオブジェクトを生成
          return {
            ...step,
            approvers:
              step.approvers?.map((approver, j) => {
                // 対象の承認者以外はそのまま返す
                if (j !== approverIndex) {
                  return approver
                }

                // 対象ステップ内の対象の承認者を更新
                return {
                  ...approver,
                  id: value as number,
                  name: users.find(user => user.id === value)?.name || '',
                }
              }) ?? [],
          }
        })

        setFieldValue(`steps`, newValue)
      }
    },
    [setFieldValue, steps, users],
  )

  return (
    <Stack spacing={2}>
      {steps?.map((step: ApprovalFlowStep, stepIndex: number) => (
        <Paper variant="outlined" key={stepIndex} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pl={1.5}
            pb={2}
          >
            {/* 承認番号。Badge の下地として Box を使用 */}
            <Box>
              <Badge badgeContent={stepIndex + 1} color="default" />
            </Box>

            {/* 承認フロー中のステップ削除、2ステップ目以降のみ使用可能 */}
            {stepIndex !== 0 && (
              <IconButton
                size="small"
                onClick={() => {
                  setFieldValue(
                    'steps',
                    (steps ?? []).filter((_, i: number) => i !== stepIndex),
                  )
                }}
              >
                <RemoveCircleOutlineIcon color="primary" />
              </IconButton>
            )}
          </Stack>
          <Stack spacing={1}>
            {step?.approvers?.map((approver, approverIndex) => (
              <Stack key={approverIndex} alignItems="center" spacing={1}>
                <Autocomplete
                  fullWidth
                  options={users}
                  getOptionLabel={option => option.name ?? ''}
                  value={users.find(u => u.id === approver.id) ?? null}
                  onChange={(_, newValue) => {
                    onChangeApprover(
                      stepIndex,
                      approverIndex,
                    )(newValue?.id ?? null)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="承認者"
                      helperText={
                        approver.id && !users.find(u => u.id === approver.id)
                          ? approver.name
                            ? `${approver.name}（削除済み）`
                            : '削除済みのユーザー'
                          : undefined
                      }
                      error={
                        Boolean(approver.id) &&
                        !users.find(u => u.id === approver.id)
                      }
                    />
                  )}
                />
                <Typography fontWeight="bold">or</Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" justifyContent="center" spacing={1}>
            {/* 承認フロー中のステップ内での誰かが承認すれば良い場合の承認者を追加 */}
            <IconButton
              size="small"
              onClick={() => {
                setFieldValue(
                  'steps',
                  steps
                    ?.filter(step => step.approvers)
                    ?.map((step, i) => {
                      if (i === stepIndex) {
                        return {
                          ...step,
                          approvers: [...(step.approvers ?? []), {} as User],
                        }
                      }
                      return step
                    }),
                )
              }}
            >
              <AddCircleOutlineIcon color="primary" />
            </IconButton>
            {/* 承認フロー中のステップ内での誰かが承認すれば良い場合の承認者を削除 */}
            {(step.approvers ?? []).length > 1 && (
              <IconButton
                size="small"
                onClick={() => {
                  setFieldValue(
                    'steps',
                    steps
                      ?.filter(step => step.approvers)
                      ?.map((step, i) => {
                        if (i === stepIndex) {
                          return {
                            ...step,
                            approvers: (step.approvers ?? []).slice(0, -1),
                          }
                        }
                        return step
                      }),
                  )
                }}
              >
                <RemoveCircleOutlineIcon color="primary" />
              </IconButton>
            )}
          </Stack>
        </Paper>
      ))}
      {/* 承認フロー中のステップ追加 */}
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        fullWidth
        onClick={() => {
          setFieldValue('steps', [...(steps ?? []), { approvers: [{}] }])
        }}
      >
        承認ステップを追加
      </Button>
    </Stack>
  )
}

export default EditApprovalFlowSteps
